/**
 * ============================================================================
 * CardNodeView — NodeView React do bloco `card` (dentro de `cardGrid`).
 * Permite editar título, descrição e link diretamente no editor.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (edição inline de card).
 * ============================================================================
 */
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useState } from "react";
import { Link2, Trash2 } from "lucide-react";

export function CardNodeView({ node, updateAttributes, deleteNode, editor }: NodeViewProps) {
  const [editingHref, setEditingHref] = useState(false);
  const [hrefDraft, setHrefDraft] = useState(String(node.attrs.href ?? ""));
  const editable = editor?.isEditable ?? true;

  const title = String(node.attrs.title ?? "");
  const description = String(node.attrs.description ?? "");

  return (
    <NodeViewWrapper
      data-type="card"
      className="kb-card kb-card--editable group relative"
    >
      <div
        className="kb-card-title outline-none"
        contentEditable={editable}
        suppressContentEditableWarning
        data-placeholder="Título do cartão"
        onBlur={(e) => updateAttributes({ title: (e.target as HTMLElement).innerText.trim() })}
      >
        {title}
      </div>
      <div
        className="kb-card-description outline-none"
        contentEditable={editable}
        suppressContentEditableWarning
        data-placeholder="Descrição do cartão"
        onBlur={(e) => updateAttributes({ description: (e.target as HTMLElement).innerText.trim() })}
      >
        {description}
      </div>

      {editable && (
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-1 right-1 flex items-center gap-0.5"
          contentEditable={false}
        >
          <button
            type="button"
            title="Definir link"
            className="p-1 rounded hover:bg-accent text-muted-foreground"
            onClick={() => {
              setHrefDraft(String(node.attrs.href ?? ""));
              setEditingHref(true);
            }}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Remover cartão"
            className="p-1 rounded hover:bg-destructive/10 text-destructive"
            onClick={() => deleteNode()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {editable && editingHref && (
        <div
          className="absolute z-10 top-full left-0 mt-1 flex items-center gap-1 bg-popover border rounded-md shadow-md p-1"
          contentEditable={false}
        >
          <input
            autoFocus
            className="text-xs px-2 py-1 bg-background border rounded outline-none w-64"
            placeholder="https://…"
            value={hrefDraft}
            onChange={(e) => setHrefDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                updateAttributes({ href: hrefDraft.trim() });
                setEditingHref(false);
              }
              if (e.key === "Escape") setEditingHref(false);
            }}
          />
          <button
            type="button"
            className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground"
            onClick={() => {
              updateAttributes({ href: hrefDraft.trim() });
              setEditingHref(false);
            }}
          >
            OK
          </button>
        </div>
      )}

      {node.attrs.href && (
        <div className="mt-1 text-[10px] text-muted-foreground truncate" contentEditable={false}>
          → {String(node.attrs.href)}
        </div>
      )}
    </NodeViewWrapper>
  );
}
